import React, { useState } from 'react';
import { useGoogleDriveStatus, useGoogleDriveVideos, useAssignVideoToLesson } from '../hooks/useGoogleDrive';
import { useAdminLessons, useUpdateLessonTitle } from '../hooks/useAdminLessons';

interface AdminVideosTabProps {
  onRefresh?: () => void;
}

const AdminVideosTab: React.FC<AdminVideosTabProps> = ({ onRefresh }) => {
  const { status: driveStatus, loading: statusLoading, refetch: refetchStatus } = useGoogleDriveStatus();
  const { videos, loading: videosLoading, error: videosError, refetch: refetchVideos } = useGoogleDriveVideos();
  const { assignVideo, loading: assignLoading } = useAssignVideoToLesson();
  const { lessons: adminLessons, loading: lessonsLoading, refetch: refetchLessons } = useAdminLessons();
  const { updateTitle, loading: updateLoading } = useUpdateLessonTitle();

  const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
  const [editedTitle, setEditedTitle] = useState<string>('');

  const handleRefresh = () => {
    refetchStatus();
    refetchVideos();
    refetchLessons();
    onRefresh?.();
  };

  const handleAssignVideo = async (lessonId: number, videoId: string) => {
    try {
      await assignVideo(lessonId, videoId);
      alert(`Video successfully assigned to lesson ${lessonId}`);
      refetchLessons();
    } catch (error) {
      alert(`Assignment error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Link copied to clipboard');
  };

  const handleEditClick = (lessonId: number, currentTitle: string) => {
    setEditingLessonId(lessonId);
    setEditedTitle(currentTitle);
  };

  const handleCancelEdit = () => {
    setEditingLessonId(null);
    setEditedTitle('');
  };

  const handleSaveTitle = async (lessonId: number) => {
    if (!editedTitle.trim()) {
      alert('Title cannot be empty');
      return;
    }

    try {
      await updateTitle(lessonId, editedTitle.trim());
      alert('Lesson title successfully updated');
      setEditingLessonId(null);
      setEditedTitle('');
      refetchLessons();
    } catch (error) {
      alert(`Update error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const loading = statusLoading || videosLoading || lessonsLoading;

  if (loading) {
    return <div className="avp-loading">Loading...</div>;
  }

  return (
    <>
      {/* Google Drive Status */}
      <div className="avp-drive-status">
        <h2 className="avp-section-title">Google Drive Status</h2>
        {driveStatus ? (
          <div className={`avp-status-card ${driveStatus.success ? 'avp-status-success' : 'avp-status-error'}`}>
            <div className="avp-status-indicator">
              {driveStatus.success ? '✓' : 'X'}
            </div>
            <div className="avp-status-message">
              {driveStatus.message}
              {driveStatus.user && (
                <div className="avp-status-user">
                  Connected as: {driveStatus.user.displayName || driveStatus.user.emailAddress}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="avp-status-card avp-status-error">
            <div className="avp-status-indicator">X</div>
            <div className="avp-status-message">Failed to check status</div>
          </div>
        )}
      </div>

      {videosError && (
        <div className="avp-error-block">
          <h3 className="avp-error-title">Error</h3>
          <p className="avp-error-text">{videosError}</p>
        </div>
      )}

      {/* Videos from Google Drive */}
      {driveStatus?.success && (
        <div className="avp-videos-section">
          <h2 className="avp-section-title">Videos from Google Drive ({videos.length})</h2>

          {videos.length === 0 ? (
            <div className="avp-no-videos">
              <p>No videos found in Google Drive folder</p>
              <p>Make sure the "AgeBack Videos/lessons" folder contains video files</p>
            </div>
          ) : (
            <div className="avp-videos-grid">
              {videos.map((video) => (
                <div key={video.id} className="avp-video-card">
                  <div className="avp-video-info">
                    <h3 className="avp-video-name">{video.name}</h3>
                    <div className="avp-video-meta">
                      <span className="avp-video-size">
                        {video.size ? `${Math.round(parseInt(video.size) / 1024 / 1024)} MB` : 'N/A'}
                      </span>
                      <span className="avp-video-date">
                        {new Date(video.createdTime).toLocaleDateString('en-US')}
                      </span>
                    </div>
                  </div>

                  <div className="avp-video-actions">
                    <button
                      className="avp-action-btn avp-action-copy"
                      onClick={() => copyToClipboard(video.directLink)}
                      title="Copy direct link"
                    >
                      Copy Link
                    </button>

                    <a
                      href={video.webViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="avp-action-btn avp-action-view"
                    >
                      View
                    </a>
                  </div>

                  <div className="avp-video-assign">
                    <h4 className="avp-assign-title">Assign to lesson:</h4>
                    <div className="avp-assign-buttons">
                      {adminLessons.map((lesson) => {
                        const isAssigned = lesson.driveVideoId === video.id;
                        return (
                          <button
                            key={lesson.id}
                            className={`avp-assign-btn ${isAssigned ? 'avp-assign-active' : ''}`}
                            onClick={() => handleAssignVideo(lesson.id, video.id)}
                            title={isAssigned ? 'Already assigned' : `Assign to lesson ${lesson.id}`}
                          >
                            {isAssigned && '✓ '}Lesson {lesson.id}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Lesson Titles */}
      <div className="avp-edit-titles-section">
        <h2 className="avp-section-title">Edit Lesson Titles</h2>
        <div className="avp-edit-titles-list">
          {adminLessons.map((lesson) => (
            <div key={lesson.id} className="avp-edit-title-card">
              <div className="avp-edit-title-header">
                <span className="avp-edit-lesson-badge">{lesson.category}</span>
                <span className="avp-edit-lesson-id">Lesson {lesson.id}</span>
              </div>

              {editingLessonId === lesson.id ? (
                <div className="avp-edit-title-form">
                  <input
                    type="text"
                    className="avp-edit-title-input"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder="Enter lesson title"
                    autoFocus
                  />
                  <div className="avp-edit-title-actions">
                    <button
                      className="avp-edit-btn avp-edit-save"
                      onClick={() => handleSaveTitle(lesson.id)}
                      disabled={updateLoading}
                    >
                      {updateLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="avp-edit-btn avp-edit-cancel"
                      onClick={handleCancelEdit}
                      disabled={updateLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="avp-edit-title-display">
                  <h3 className="avp-edit-current-title">{lesson.title}</h3>
                  <button
                    className="avp-edit-btn avp-edit-start"
                    onClick={() => handleEditClick(lesson.id, lesson.title)}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lesson Configuration */}
      <div className="avp-lessons-section">
        <h2 className="avp-section-title">Lesson Configuration</h2>
        <div className="avp-lessons-list">
          {adminLessons.map((lesson) => (
            <div key={lesson.id} className="avp-lesson-card">
              <div className="avp-lesson-top">
                <h3 className="avp-lesson-title">Lesson {lesson.id}: {lesson.title}</h3>
                <span className="avp-lesson-badge">{lesson.category}</span>
              </div>
              <div className="avp-lesson-desc">
                {lesson.description}
              </div>
              <div className="avp-lesson-ids">
                <div className="avp-id-row">
                  <label className="avp-id-label">Video ID:</label>
                  <span className="avp-id-value">{lesson.driveVideoId || 'Not assigned'}</span>
                </div>
                <div className="avp-id-row">
                  <label className="avp-id-label">Thumbnail ID:</label>
                  <span className="avp-id-value">{lesson.driveThumbnailId || 'Not assigned'}</span>
                </div>
                <div className="avp-id-row">
                  <label className="avp-id-label">Preview ID:</label>
                  <span className="avp-id-value">{lesson.drivePreviewId || 'Not assigned'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="avp-instructions">
        <div className="avp-instructions-content">
          <h3 className="avp-instructions-subtitle">Environment Variables</h3>
          <ul className="avp-instructions-list">
            <li>GOOGLE_SERVICE_ACCOUNT_KEY - Service Account JSON key</li>
            <li>GOOGLE_DRIVE_FOLDER_ID - "AgeBack Videos" folder ID</li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default AdminVideosTab;
